import { AgentState, AgentStates, MediaType } from '@dxp/core/types';
import {
    calculateWithPercentage,
    convertToMediaTypeRecord,
    aggregateWorkItemsPerMediaType,
} from '@dxp/core/helpers';
import { IAgent } from '../agent/agent';
import { IQueue } from '../queue/queue';
import {
    ITelXLKpiTeamThreshold,
    TelXLKpiTeamThresholdDefaultState,
} from '../rbac/team-kpi';
import { IHandledCountKpi, IWorkItemKpis } from '../workitem/workitem';
import { IValueWithMetric } from '../metrics/metric';
import { IMenuItemsData } from '../menu/menu-items';
import { IKeyValuePair } from '../application/key-value';

export interface ITeam {
    id: string;
    name: string;
    agents: IAgent[];
    activeAgents: IAgent[];
    queues: IQueue[];
    status: {
        loggedOut: number;
        idle: number;
        break: number;
        busy: number;
        online: number;
        total: number;
    };
    businessUnits: string[];
    hasActivity: boolean;
    kpiTelXLThreshold: ITelXLKpiTeamThreshold;
    statistics: ITeamStatistics;
}

export interface ITeamStatistics {
    totalTimeLoggedIn: number;
    timeSpentInAgentStates: Record<
        AgentState,
        { value: number; percentage: number }
    >;
    timeSpentInBreakNames: Record<
        string,
        { value: number; percentage: number }
    >;
    workItemDispositionCodes: Record<
        string,
        { value: number; percentage: number }
    >;
    workItemsPerMediaType: Record<
        MediaType,
        {
            split: number;
            handledCount: number;
            missedCount: number;
            averageHandlingTimeInSeconds: IValueWithMetric;
            ratio: number;
        }
    >;
    tasks: {
        handled: number;
        missed: number;
        ratio: number;
        averageHandlingTimeInSeconds: IValueWithMetric;
    };
    timeSpentInWrap: { value: number; percentage: number };
}

export interface ITeamPerformanceVm {
    menuItemsData: IMenuItemsData[];
    teamVm: ITeamVm;
}

export interface ITeamVm {
    name: string;
    statistics: {
        tasks: {
            handled: number;
            missed: number;
            averageHandlingTimeInSeconds: {
                value: number;
                metric: object;
            };
        };
    };
    status: {
        total: number;
        online: number;
        idle: number;
        busy: number;
        break: number;
    };
}

export class Team implements ITeam {
    readonly id: string;
    readonly name: string;
    readonly #agents: IAgent[];
    readonly #queues: IQueue[];
    readonly #kpiTelXLThreshold: ITelXLKpiTeamThreshold;

    constructor(
        id: string,
        name: string,
        agents: IAgent[],
        queues: IQueue[],
        kpiTelXLThreshold?: ITelXLKpiTeamThreshold,
    ) {
        this.id = id;
        this.name = name;
        this.#agents = agents;
        this.#queues = queues;
        this.#kpiTelXLThreshold =
            kpiTelXLThreshold ?? TelXLKpiTeamThresholdDefaultState;
    }

    get hasActivity(): boolean {
        return this.activeAgents.length > 0;
    }

    get agents(): IAgent[] {
        return this.#agents;
    }

    get activeAgents(): IAgent[] {
        return this.#agents.filter(a => a.hasActivity);
    }

    get businessUnits(): string[] {
        return [...new Set(this.#agents.flatMap(a => a.businessUnits))];
    }

    get queues(): IQueue[] {
        return this.#queues;
    }

    get kpiTelXLThreshold(): ITelXLKpiTeamThreshold {
        return this.#kpiTelXLThreshold;
    }

    get status(): {
        loggedOut: number;
        idle: number;
        break: number;
        busy: number;
        online: number;
        total: number;
    } {
        const status = this.activeAgents.map(a => a.status);

        const aggregate = status.reduce(
            (current, next) => {
                switch (next.state) {
                    case 'Busy':
                        current.busy += 1;
                        current.online += 1;
                        break;
                    case 'Idle':
                        current.idle += 1;
                        current.online += 1;
                        break;
                    case 'Break':
                        current.break += 1;
                        current.online += 1;
                        break;
                    case 'LoggedOut':
                        current.loggedOut += 1;
                        break;
                }
                return current;
            },
            {
                loggedOut: 0,
                idle: 0,
                break: 0,
                busy: 0,
                online: 0,
                total: this.agents.length,
            },
        );

        return { ...aggregate, total: this.agents.length };
    }

    get statistics(): ITeamStatistics {
        const aggregatedTimeSpentInAgentStates =
            this.#aggregateAgentData<number>(
                agent => agent.statistics.timeSpentInAgentStates,
                next => next.value,
                (current, next) => current + next.value,
            );

        const aggregatedTimeSpentInBreakNames =
            this.#aggregateAgentData<number>(
                agent => agent.statistics.timeSpentInBreakNames,
                next => next.value,
                (current, next) => current + next.value,
            );

        const aggregatedWorkItemKpisPerDispositionCode =
            this.#aggregateAgentData<IHandledCountKpi>(
                agent => agent.statistics.workItemDispositionCodes,
                next => ({ handledCount: next.value }),
                (current, next) => ({
                    handledCount: current.handledCount + next.value,
                }),
            );

        const aggregatedworkItemsPerMediaType =
            this.#aggregateWorkItemsPerMediaType();

        const aggregateTimeSpentInWrap = this.#aggregateAgentTimeSpentInWrap();
        const aggregateTimeSpentInBusy =
            aggregatedTimeSpentInAgentStates.find(as => as.key === 'Busy')
                ?.value ?? 0;

        return {
            totalTimeLoggedIn: this.#aggregateAgentTotalTimeLoggedIn(),
            timeSpentInAgentStates: calculateWithPercentage(
                AgentStates.filter(
                    as => as !== 'LoggedOut',
                ) as readonly string[],
                aggregatedTimeSpentInAgentStates,
                value => value,
            ),
            timeSpentInBreakNames: calculateWithPercentage(
                aggregatedTimeSpentInBreakNames.map(kvp => kvp.key) ?? [],
                aggregatedTimeSpentInBreakNames ?? [],
                value => value,
            ),
            workItemDispositionCodes: calculateWithPercentage(
                aggregatedWorkItemKpisPerDispositionCode.map(kvp => kvp.key) ??
                    [],
                aggregatedWorkItemKpisPerDispositionCode ?? [],
                item => item.handledCount,
            ),
            workItemsPerMediaType: convertToMediaTypeRecord(
                aggregatedworkItemsPerMediaType ?? [],
                this.#kpiTelXLThreshold.averageHandlingTimeSeconds,
            ),
            tasks: aggregateWorkItemsPerMediaType(
                aggregatedworkItemsPerMediaType ?? [],
                this.#kpiTelXLThreshold.averageHandlingTimeSeconds.average,
            ),
            timeSpentInWrap: {
                value: aggregateTimeSpentInWrap,
                percentage:
                    aggregateTimeSpentInBusy > 0
                        ? Math.round(
                              (aggregateTimeSpentInWrap /
                                  aggregateTimeSpentInBusy) *
                                  100,
                          )
                        : 0,
            },
        };
    }

    #aggregateAgentData<T>(
        getAgentData: (agent: IAgent) => Record<string, { value: any }>,
        createFunction: (next: { value: any }) => T,
        mergeFunction: (current: T, next: { value: any }) => T,
    ): IKeyValuePair<T>[] {
        const aggregatedData: IKeyValuePair<T>[] = [];

        this.activeAgents.forEach(agent => {
            const agentData = getAgentData(agent);
            Object.entries(agentData).forEach(([key, data]) => {
                const existingEntry = aggregatedData.find(a => a.key === key);
                if (!existingEntry) {
                    aggregatedData.push({ key, value: createFunction(data) });
                } else {
                    existingEntry.value = mergeFunction(
                        existingEntry.value,
                        data,
                    );
                }
            });
        });

        return aggregatedData;
    }

    #aggregateWorkItemsPerMediaType(): IKeyValuePair<IWorkItemKpis>[] {
        const aggregatedData: IKeyValuePair<IWorkItemKpis>[] = [];
        const averaging: IKeyValuePair<number>[] = [];

        this.activeAgents.forEach(agent => {
            const agentData = agent.statistics.workItemsPerMediaType;

            Object.entries(agentData).forEach(([key, data]) => {
                const existingEntry = aggregatedData.find(
                    a => a.key === (key as MediaType),
                );

                if (!existingEntry) {
                    aggregatedData.push({
                        key: key as MediaType,
                        value: {
                            handledCount: data.handledCount,
                            missedCount: data.missedCount,
                            averageHandlingTimeInSeconds:
                                data.averageHandlingTimeInSeconds.value,
                        },
                    });
                } else {
                    existingEntry.value.handledCount += data.handledCount;
                    existingEntry.value.missedCount += data.missedCount;
                    existingEntry.value.averageHandlingTimeInSeconds +=
                        data.averageHandlingTimeInSeconds.value;
                }

                const existingAveragingEntry = averaging.find(
                    a => a.key === (key as MediaType),
                );

                if (!existingAveragingEntry) {
                    averaging.push({
                        key: key as MediaType,
                        value:
                            data.averageHandlingTimeInSeconds.value > 0 ? 1 : 0,
                    });
                } else {
                    existingAveragingEntry.value +=
                        data.averageHandlingTimeInSeconds.value > 0 ? 1 : 0;
                }
            });
        });

        aggregatedData.forEach(ad => {
            const average = averaging.find(a => a.key === ad.key);

            if (average && average.value > 0) {
                ad.value.averageHandlingTimeInSeconds /= average.value;
            }
        });

        return aggregatedData;
    }

    #aggregateAgentTotalTimeLoggedIn(): number {
        return this.activeAgents.reduce(
            (total, agent) => total + agent.statistics.totalTimeLoggedIn,
            0,
        );
    }

    #aggregateAgentTimeSpentInWrap(): number {
        return this.activeAgents.reduce(
            (total, agent) => total + agent.statistics.timeSpentInWrap.value,
            0,
        );
    }
}
